import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Slide,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { CATEGORY_COLORS } from '../../constants/categoryColors';
import { getQuickTagGroups } from '../../constants/logTypeRegistry';
import { QUICK_TAG_CATEGORY_MAP } from '../../utils/quickNoteClassification';
import {
  QUICK_TAG_EMOJI_CHOICES,
  createCustomQuickTag,
  getAllQuickTagOptions,
  loadCustomQuickTags,
  saveCustomQuickTags,
} from '../../utils/quickTags';

const QuickTagsSection = ({
  showTagSection = true,
  selectedTags = [],
  setSelectedTags,
  importantMoment = false,
  setImportantMoment,
  userId = null,
}) => {
  const [showQuickTags, setShowQuickTags] = useState(false);
  const [customQuickTags, setCustomQuickTags] = useState([]);
  const [tagDraftOpen, setTagDraftOpen] = useState(false);
  const [tagDraftLabel, setTagDraftLabel] = useState('');
  const [tagDraftEmoji, setTagDraftEmoji] = useState(QUICK_TAG_EMOJI_CHOICES[0]);
  const quickTagTouchStartYRef = useRef(null);

  useEffect(() => {
    setCustomQuickTags(loadCustomQuickTags(userId));
  }, [userId]);

  const quickTagGroups = useMemo(() => {
    const builtInGroups = getQuickTagGroups();
    const customGroup = customQuickTags.length > 0
      ? {
          id: 'custom-tags',
          label: 'Your tags',
          items: customQuickTags,
        }
      : null;

    return [...builtInGroups, customGroup].filter(Boolean);
  }, [customQuickTags]);

  const quickTagLookup = useMemo(() => {
    const registry = new Map();
    getAllQuickTagOptions(customQuickTags).forEach((tag) => {
      registry.set(tag.key, tag);
    });
    return registry;
  }, [customQuickTags]);

  const primaryTagMeta = selectedTags.length > 0 ? quickTagLookup.get(selectedTags[0]) : null;

  const toggleQuickTag = (tagKey) => {
    setSelectedTags((prev) => (
      prev.includes(tagKey)
        ? prev.filter((key) => key !== tagKey)
        : [...prev, tagKey]
    ));
  };

  const handleQuickTagSelect = (tagKey) => {
    toggleQuickTag(tagKey);
  };

  const handleCreateQuickTag = () => {
    const label = tagDraftLabel.trim();
    if (!label) {
      return;
    }

    const nextTag = createCustomQuickTag(label, tagDraftEmoji);
    if (!nextTag.key) {
      return;
    }

    setCustomQuickTags((prev) => {
      const existingIndex = prev.findIndex((tag) => tag.key === nextTag.key);
      const nextTags = existingIndex >= 0
        ? prev.map((tag) => (tag.key === nextTag.key ? { ...tag, label: nextTag.label, icon: nextTag.icon } : tag))
        : [...prev, nextTag];

      saveCustomQuickTags(userId, nextTags);
      return nextTags;
    });

    setSelectedTags((prev) => (prev.includes(nextTag.key) ? prev : [...prev, nextTag.key]));
    setTagDraftLabel('');
    setTagDraftEmoji(QUICK_TAG_EMOJI_CHOICES[0]);
    setTagDraftOpen(false);
  };

  const closeQuickTags = () => {
    setShowQuickTags(false);
  };

  const handleQuickTagTouchStart = (event) => {
    quickTagTouchStartYRef.current = event.touches?.[0]?.clientY ?? null;
  };

  const handleQuickTagTouchEnd = (event) => {
    const touchStartY = quickTagTouchStartYRef.current;
    const touchEndY = event.changedTouches?.[0]?.clientY ?? null;
    quickTagTouchStartYRef.current = null;

    if (touchStartY == null || touchEndY == null) {
      return;
    }

    if (touchEndY - touchStartY > 50) {
      closeQuickTags();
    }
  };

  if (!showTagSection) {
    return null;
  }

  return (
    <>
      {selectedTags.length > 0 && (
        <Box
          sx={{
            mt: 1,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
          }}
        >
          {selectedTags.map((tag) => {
            const tagMeta = quickTagLookup.get(tag);
            const categoryKey = QUICK_TAG_CATEGORY_MAP[tag] || 'log';
            const categoryColors = tagMeta?.custom
              ? {
                  bg: '#F8FAFC',
                  text: '#334155',
                  border: '#C7D9C4',
                }
              : (CATEGORY_COLORS[categoryKey] || CATEGORY_COLORS.log);
            return (
              <Chip
                key={tag}
                label={tagMeta?.label || tag}
                onDelete={() => toggleQuickTag(tag)}
                sx={{
                  borderRadius: '999px',
                  bgcolor: categoryColors.bg,
                  color: categoryColors.text,
                  border: `1px solid ${categoryColors.border}`,
                  fontWeight: 700,
                  '& .MuiChip-deleteIcon': {
                    color: categoryColors.text,
                    fontSize: 16,
                  },
                }}
              />
            );
          })}
        </Box>
      )}

      <Box
        sx={{
          mt: 1.5,
          mb: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: 1,
            width: '100%',
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setImportantMoment((prev) => !prev)}
            sx={{
              minHeight: 42,
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              color: '#6a7280',
              borderColor: '#dfe3ea',
              bgcolor: '#f7f8fa',
              px: 1,
              py: 0.75,
              width: '100%',
              minWidth: 0,
              '&:hover': {
                bgcolor: '#f0f2f6',
                borderColor: '#d3d9e2',
              },
            }}
          >
            <Box component="span" sx={{ fontSize: '0.9rem', mr: 0.4, lineHeight: 1 }}>
              ⭐
            </Box>
            <Box component="span" sx={{ lineHeight: 1, whiteSpace: 'nowrap', fontWeight: 650 }}>
              {importantMoment ? 'Important On' : 'Flag Important'}
            </Box>
          </Button>

          <Button
            variant="outlined"
            startIcon={<LocalOfferIcon sx={{ color: '#102d72' }} />}
            endIcon={<ExpandMoreIcon sx={{ transform: showQuickTags ? 'rotate(180deg)' : 'none', transition: 'all 0.2s', color: '#102d72' }} />}
            onClick={() => setShowQuickTags((prev) => !prev)}
            sx={{
              minHeight: 42,
              borderRadius: '10px',
              borderColor: '#dfe3ea',
              color: '#6a7280',
              px: 1,
              py: 0.75,
              width: '100%',
              minWidth: 0,
              textTransform: 'none',
              fontSize: '0.82rem',
              fontWeight: 650,
              bgcolor: '#f7f8fa',
              '&:hover': {
                bgcolor: '#f0f2f6',
                borderColor: '#d3d9e2',
              },
              '& .MuiButton-startIcon': {
                mr: 0.25,
                ml: 0,
              },
              '& .MuiButton-endIcon': {
                ml: 0.25,
              },
            }}
          >
            {selectedTags.length > 0
              ? selectedTags.length === 1
                ? `Tags: ${primaryTagMeta?.label || selectedTags[0]}`
                : `Tags (${selectedTags.length})`
              : 'Add context'}
          </Button>
        </Box>
      </Box>

      <Slide direction="up" in={showQuickTags} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 3,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(15, 23, 42, 0.2)',
            }}
            onClick={closeQuickTags}
          >
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                px: 1.5,
                pb: 1.5,
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <Box
                sx={{
                  borderRadius: '18px 18px 12px 12px',
                  border: '1px solid #d8def0',
                  bgcolor: '#ffffff',
                  boxShadow: '0 -18px 40px rgba(31, 41, 55, 0.16)',
                  p: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    mb: 1.25,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                    onTouchStart={handleQuickTagTouchStart}
                    onTouchEnd={handleQuickTagTouchEnd}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 5,
                        borderRadius: 999,
                        bgcolor: '#d5d9e3',
                      }}
                    />
                  </Box>

                  <IconButton
                    size="small"
                    onClick={closeQuickTags}
                    sx={{
                      ml: 1,
                      mt: -0.5,
                      color: '#6b7280',
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>

                <Typography sx={{ fontWeight: 800, fontSize: '0.98rem', color: '#283142', mb: 1.25 }}>
                  Pick a quick tag
                </Typography>

                <Stack spacing={1} sx={{ width: '100%' }}>
                  {quickTagGroups.map((group, groupIndex) => (
                    <Box
                      key={groupIndex}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 140px))' },
                        gap: 1,
                        width: '100%',
                      }}
                    >
                      {group.items.map((item) => {
                        const selected = selectedTags.includes(item.key);
                        const categoryKey = QUICK_TAG_CATEGORY_MAP[item.key] || 'log';
                        const categoryColors = CATEGORY_COLORS[categoryKey] || CATEGORY_COLORS.log;
                        return (
                          <Chip
                            key={item.key}
                            icon={<Box component="span" sx={{ fontSize: '1.1rem' }}>{item.icon}</Box>}
                            label={selected ? `✓ ${item.label}` : item.label}
                            onClick={() => handleQuickTagSelect(item.key)}
                            sx={{
                              borderRadius: '10px',
                              border: `2px solid ${categoryColors.border}`,
                              bgcolor: selected ? categoryColors.border : categoryColors.bg,
                              color: selected ? '#ffffff' : categoryColors.text,
                              width: '100%',
                              maxWidth: { xs: '100%', sm: 140 },
                              justifyContent: 'flex-start',
                              px: 0.5,
                              height: 46,
                              boxShadow: selected ? `0 10px 22px ${categoryColors.border}40` : 'none',
                              '& .MuiChip-label': {
                                px: 0.5,
                                width: '100%',
                                fontSize: '0.9rem',
                                fontWeight: 700,
                                color: selected ? '#ffffff' : categoryColors.text,
                                textAlign: 'left',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                              '& .MuiChip-icon': {
                                ml: 0.75,
                                mr: 0.25,
                                fontSize: 21,
                                opacity: selected ? 1 : 0.92,
                                color: selected ? '#ffffff' : categoryColors.text,
                              },
                              '&:hover': {
                                bgcolor: selected ? categoryColors.border : categoryColors.bg,
                                borderColor: categoryColors.border,
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                  ))}
                </Stack>

                {!tagDraftOpen ? (
                  <Box sx={{ mt: 1.25, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      onClick={() => setTagDraftOpen(true)}
                      aria-label="Add a new quick tag"
                      variant="outlined"
                      sx={{
                        minWidth: 40,
                        width: 40,
                        height: 40,
                        borderRadius: '12px',
                        borderWidth: '1px',
                        borderColor: '#C7D9C4',
                        color: '#334155',
                        bgcolor: '#ffffff',
                        textTransform: 'none',
                        fontWeight: 900,
                        boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
                        '&:hover': {
                          borderWidth: '1px',
                          borderColor: '#B8D3B5',
                          bgcolor: '#F7FBF9',
                        },
                      }}
                    >
                      +
                    </Button>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      mt: 1.25,
                      p: 1.2,
                      borderRadius: '14px',
                      border: '1px solid #E2E8F0',
                      bgcolor: '#F8FAFC',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#64748B', mb: 1 }}>
                      Add your own tag
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={tagDraftLabel}
                      onChange={(event) => setTagDraftLabel(event.target.value)}
                      placeholder="1-2 words"
                      helperText="Keep it short so it stays quick."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          bgcolor: '#ffffff',
                        },
                      }}
                    />

                    <Box sx={{ mt: 1 }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#64748B', mb: 0.75 }}>
                        Pick an emoji
                      </Typography>
                      <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
                        {QUICK_TAG_EMOJI_CHOICES.map((emoji) => (
                          <Chip
                            key={emoji}
                            label={emoji}
                            onClick={() => setTagDraftEmoji(emoji)}
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '12px',
                              border: `1px solid ${tagDraftEmoji === emoji ? '#8FC9C0' : '#E2E8F0'}`,
                              bgcolor: tagDraftEmoji === emoji ? '#EAF4F2' : '#ffffff',
                              fontSize: '1rem',
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, mt: 1.2 }}>
                      <Button
                        fullWidth
                        variant="text"
                        onClick={() => {
                          setTagDraftOpen(false);
                          setTagDraftLabel('');
                          setTagDraftEmoji(QUICK_TAG_EMOJI_CHOICES[0]);
                        }}
                        sx={{
                          textTransform: 'none',
                          borderRadius: '12px',
                          color: '#64748B',
                          fontWeight: 700,
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleCreateQuickTag}
                        disabled={!tagDraftLabel.trim()}
                        sx={{
                          textTransform: 'none',
                          borderRadius: '12px',
                          bgcolor: '#8FC9C0',
                          color: '#0F172A',
                          fontWeight: 800,
                          '&:hover': {
                            bgcolor: '#7EB8AF',
                          },
                          '&.Mui-disabled': {
                            bgcolor: '#E2E8F0',
                            color: '#94A3B8',
                          },
                        }}
                      >
                        Add tag
                      </Button>
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                  <Button
                    variant="contained"
                    onClick={closeQuickTags}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '10px',
                      px: 2,
                      bgcolor: '#102d72',
                      '&:hover': { bgcolor: '#0b255d' },
                    }}
                  >
                    Done
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Slide>
    </>
  );
};

export default QuickTagsSection;
